import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LevelCard } from '../components/level';
import {
  getExpHistory,
  getMyRanking,
  getMyAchievements,
  getDailyQuests,
  claimQuest,
  claimAllQuests,
  checkAchievements,
  getNicknameStatus,
  validateNickname,
  getRandomNickname,
  setNickname
} from '../services/level';
import './Profile.css';

/**
 * 프로필 페이지
 * - 사용자 정보, 레벨, 경험치 내역, 랭킹, 업적 표시
 */
const Profile = () => {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [expHistory, setExpHistory] = useState([]);
  const [ranking, setRanking] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [quests, setQuests] = useState(null);
  const [loading, setLoading] = useState(true);

  // 닉네임 관련 상태
  const [nicknameStatus, setNicknameStatus] = useState(null);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [nicknameError, setNicknameError] = useState('');
  const [nicknameSaving, setNicknameSaving] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [expRes, rankRes, achieveRes, questRes] = await Promise.all([
        getExpHistory(10, 0).catch(() => ({ logs: [] })),
        getMyRanking().catch(() => null),
        getMyAchievements().catch(() => null),
        getDailyQuests().catch(() => null)
      ]);

      setExpHistory(expRes.logs || []);
      setRanking(rankRes);
      setAchievements(achieveRes);
      setQuests(questRes);
    } catch (err) {
      console.error('Failed to fetch profile data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimQuest = async (questType) => {
    try {
      await claimQuest(questType);
      // 퀘스트 새로고침
      const questRes = await getDailyQuests();
      setQuests(questRes);
    } catch (err) {
      console.error('Failed to claim quest:', err);
    }
  };

  const handleClaimAll = async () => {
    try {
      await claimAllQuests();
      const questRes = await getDailyQuests();
      setQuests(questRes);
    } catch (err) {
      console.error('Failed to claim all quests:', err);
    }
  };

  const handleCheckAchievements = async () => {
    try {
      const result = await checkAchievements();
      if (result.newlyUnlocked?.length > 0) {
        alert(`새로운 업적 달성! ${result.newlyUnlocked.map(a => a.name).join(', ')}`);
        const achieveRes = await getMyAchievements();
        setAchievements(achieveRes);
      }
    } catch (err) {
      console.error('Failed to check achievements:', err);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="profile-page">
        <div className="profile-login-required">
          <p>로그인이 필요합니다.</p>
          <a href="https://ilouli.com/login" className="profile-login-btn">로그인</a>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('ko-KR', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Seoul'
    });
  };

  const expTypeLabels = {
    login: '출석',
    write_post: '게시글',
    write_comment: '댓글',
    receive_like: '추천받음',
    give_like: '추천',
    quest_complete: '퀘스트',
    achievement: '업적'
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-avatar">
          {user?.picture ? (
            <img src={user.picture} alt={user.displayName || user.nickname} />
          ) : (
            <span>{(user?.displayName || user?.nickname)?.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="profile-info">
          <h1 className="profile-name">{user?.displayName || user?.nickname}</h1>
          <p className="profile-email">{user?.email}</p>
          {/* PRD: 프로필 수정은 auth.ilouli.com으로 */}
          <a
            href="https://auth.ilouli.com/profile"
            className="profile-edit-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            프로필 수정 →
          </a>
        </div>
      </div>

      <div className="profile-content">
        {/* 레벨 카드 */}
        <div className="profile-section">
          <LevelCard />
        </div>

        {/* 탭 네비게이션 */}
        <div className="profile-tabs">
          <button
            className={`profile-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            개요
          </button>
          <button
            className={`profile-tab ${activeTab === 'quests' ? 'active' : ''}`}
            onClick={() => setActiveTab('quests')}
          >
            일일 퀘스트
          </button>
          <button
            className={`profile-tab ${activeTab === 'achievements' ? 'active' : ''}`}
            onClick={() => setActiveTab('achievements')}
          >
            업적
          </button>
          <button
            className={`profile-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            경험치 내역
          </button>
        </div>

        {/* 탭 내용 */}
        <div className="profile-tab-content">
          {activeTab === 'overview' && (
            <div className="profile-overview">
              {/* 랭킹 */}
              {ranking?.ranks && (
                <div className="profile-rankings">
                  <h3>내 랭킹</h3>
                  <div className="ranking-cards">
                    <div className="ranking-card">
                      <span className="ranking-icon">🏆</span>
                      <span className="ranking-value">{ranking.ranks.level}위</span>
                      <span className="ranking-label">레벨</span>
                    </div>
                    <div className="ranking-card">
                      <span className="ranking-icon">👍</span>
                      <span className="ranking-value">{ranking.ranks.likes}위</span>
                      <span className="ranking-label">추천</span>
                    </div>
                    <div className="ranking-card">
                      <span className="ranking-icon">🔥</span>
                      <span className="ranking-value">{ranking.ranks.streak}위</span>
                      <span className="ranking-label">연속 접속</span>
                    </div>
                  </div>
                </div>
              )}

              {/* 최근 경험치 */}
              <div className="profile-recent-exp">
                <h3>최근 경험치</h3>
                {expHistory.length > 0 ? (
                  <ul className="exp-list">
                    {expHistory.slice(0, 5).map(log => (
                      <li key={log.id} className="exp-item">
                        <span className="exp-type">{expTypeLabels[log.type] || log.type}</span>
                        <span className="exp-reason">{log.reason}</span>
                        <span className="exp-amount">+{log.amount}</span>
                        <span className="exp-date">{formatDate(log.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="empty-message">경험치 내역이 없습니다.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'quests' && (
            <div className="profile-quests">
              <div className="quests-header">
                <h3>일일 퀘스트</h3>
                {quests?.claimableCount > 0 && (
                  <button className="claim-all-btn" onClick={handleClaimAll}>
                    모두 수령 ({quests.claimableCount})
                  </button>
                )}
              </div>
              {quests?.quests?.length > 0 ? (
                <ul className="quest-list">
                  {quests.quests.map(quest => (
                    <li key={quest.type} className={`quest-item ${quest.status}`}>
                      <div className="quest-info">
                        <span className="quest-icon">{quest.icon}</span>
                        <div className="quest-details">
                          <span className="quest-name">{quest.name}</span>
                          <span className="quest-desc">{quest.description}</span>
                          <div className="quest-progress-bar">
                            <div
                              className="quest-progress-fill"
                              style={{ width: `${Math.min((quest.progress / quest.target) * 100, 100)}%` }}
                            />
                          </div>
                          <span className="quest-progress-text">
                            {quest.progress} / {quest.target}
                          </span>
                        </div>
                      </div>
                      <div className="quest-reward">
                        <span className="quest-exp">+{quest.reward} EXP</span>
                        {quest.status === 'claimable' && (
                          <button
                            className="claim-btn"
                            onClick={() => handleClaimQuest(quest.type)}
                          >
                            수령
                          </button>
                        )}
                        {quest.status === 'claimed' && (
                          <span className="claimed-badge">완료</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="empty-message">퀘스트 정보를 불러오는 중...</p>
              )}
            </div>
          )}

          {activeTab === 'achievements' && (
            <div className="profile-achievements">
              <div className="achievements-header">
                <h3>업적</h3>
                <button className="check-btn" onClick={handleCheckAchievements}>
                  업적 확인
                </button>
              </div>
              {achievements?.stats && (
                <div className="achievements-stats">
                  <span>{achievements.stats.unlocked} / {achievements.stats.total} 달성</span>
                  <span className="achievements-progress">({achievements.stats.progress}%)</span>
                </div>
              )}
              {achievements?.achievements?.length > 0 ? (
                <ul className="achievement-list">
                  {achievements.achievements.map(ach => (
                    <li key={ach.id} className={`achievement-item ${ach.unlocked ? 'unlocked' : 'locked'}`}>
                      <span className="achievement-icon">{ach.icon}</span>
                      <div className="achievement-info">
                        <span className="achievement-name">{ach.name}</span>
                        <span className="achievement-desc">{ach.description}</span>
                      </div>
                      <span className="achievement-reward">+{ach.reward} EXP</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="empty-message">업적 정보를 불러오는 중...</p>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="profile-history">
              <h3>경험치 내역</h3>
              {expHistory.length > 0 ? (
                <ul className="exp-list full">
                  {expHistory.map(log => (
                    <li key={log.id} className="exp-item">
                      <span className="exp-type">{expTypeLabels[log.type] || log.type}</span>
                      <span className="exp-reason">{log.reason}</span>
                      <span className="exp-amount">+{log.amount}</span>
                      <span className="exp-date">{formatDate(log.created_at)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="empty-message">경험치 내역이 없습니다.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
